import React from 'react';
import {observer} from 'mobx-react';
import {runInAction} from 'mobx';
import {screenResizeStore} from '../../../global/store/screen-resize-store';
import {cafDocSizeOptions, formatCafDocSize, isValidCafDocDimension} from './caf-doc-size-validation';
import s from '../../s';
import ModalSimple from '../../../global/modal-simple';
import InfoIcon from '../shared/info-icon';
import {currency, modal, openLink, Portal} from '../../../util';
import {isFixedSizeDisplay} from './document-sizing';
import DocSizeDiffModal from './doc-size-diff-modal';

interface IDocSize {
    i: number;
    w: string;
    h: string;
}

const doc_sizes: IDocSize[] = [
    {i: 0, h: '6.0', w: '8.0'},
    {i: 1, h: '7.0', w: '9.0'},
    {i: 2, h: '8.0', w: '10.0'},
    {i: 3, h: '8.5', w: '11.0'},
    {i: 4, h: '10.0', w: '12.0'},
    {i: 5, h: '11.0', w: '14.0'},
    {i: 6, h: '12.0', w: '16.0'},
    {i: 7, h: '13.0', w: '17.0'},
    {i: 8, h: '14.0', w: '18.0'},
    {i: 9, h: '16.0', w: '20.0'},
    {i: 10, h: '18.0', w: '22.0'},
    {i: 11, h: '20.0', w: '24.0'},
];

const doc_sizes_photo: IDocSize[] = [
    {i: 0, h: '5.0', w: '7.0'},
    {i: 1, h: '8.0', w: '10.0'},
];

const doc_sizes_varsity: IDocSize[] = [
    {i: 0, h: '8.0', w: '9.25'},
    {i: 1, h: '10.0', w: '12.0'},
];

/** Double Document (DYF frame style 2) carries a separate bottom document size. */
const isDoubleDocument = (): boolean => s.prod.DYF_FrameStyleID === 2;

const exceedsPlexiLimit = (w: number, h: number, shortSide: number, longSide: number): boolean => (w > shortSide && h > longSide) || (w > longSide && h > shortSide);

// Keep preset and exact-size updates on the same secondary-document/glazing rules.
// `bottom` is the desktop Double Document bottom size; without it the bottom mirrors the top.
const updateDocSizeOptions = (bottom?: {width: number; height: number}) => {
    s.ps.docWidth2 = s.ps.docWidth;
    s.ps.docHeight2 = s.ps.docHeight;

    if (!isDoubleDocument()) {
        s.ps.docWidth2 = -1;
        s.ps.docHeight2 = -1;
    } else if (bottom) {
        s.ps.docWidth2 = bottom.width;
        s.ps.docHeight2 = bottom.height;
    }

    if (s.prod.IsCAF) {
        // Glazing is decided by the largest document in the frame.
        const docs: [number, number][] = [[s.ps.docWidth, s.ps.docHeight]];
        if (isDoubleDocument() && s.ps.docWidth2 > 0 && s.ps.docHeight2 > 0) {
            docs.push([s.ps.docWidth2, s.ps.docHeight2]);
        }
        let shortSide = 14;
        let longSide = 18;
        if (s.prod.DYF_FrameStyleID === 2 || s.prod.DYF_FrameStyleID === 7 || s.prod.DYF_FrameStyleID === 8 || s.prod.DYF_FrameStyleID === 6) {
            shortSide = 9;
            longSide = 12;
        } else if (s.prod.DYF_FrameStyleID === 3 || s.prod.DYF_FrameStyleID === 5) {
            shortSide = 11;
            longSide = 14;
        }
        s.ps.use_plexi = docs.some(([w, h]) => exceedsPlexiLimit(w, h, shortSide, longSide));
    }

    if (s.prod.ForcePlexiglass) {
        s.ps.use_plexi = true;
    }
};

const DocSize: React.FC = observer(() => {
    const isDesktopCaf = s.ps.is_caf && screenResizeStore.width >= 1200;
    const [docWidth, setDocWidth] = React.useState(() => String(s.ps.docWidth));
    const [docHeight, setDocHeight] = React.useState(() => String(s.ps.docHeight));
    // Double Document bottom size. It mirrors the top size until the user picks a bottom size of their own.
    const [doc2Width, setDoc2Width] = React.useState(() => String(s.ps.docWidth2 > 0 ? s.ps.docWidth2 : s.ps.docWidth));
    const [doc2Height, setDoc2Height] = React.useState(() => String(s.ps.docHeight2 > 0 ? s.ps.docHeight2 : s.ps.docHeight));
    const doc2Linked = React.useRef(!(s.ps.docWidth2 > 0 && s.ps.docHeight2 > 0 && (s.ps.docWidth2 !== s.ps.docWidth || s.ps.docHeight2 !== s.ps.docHeight)));
    const renderTimeout = React.useRef<ReturnType<typeof setTimeout> | null>(null);

    const isValidDocSize = (w: number, h: number) => isValidCafDocDimension(w) && isValidCafDocDimension(h);

    React.useEffect(() => {
        if (!isDesktopCaf) return;
        const ps = s.ps;
        setDocWidth(String(ps.docWidth));
        setDocHeight(String(ps.docHeight));
        const bottomW = ps.docWidth2 > 0 ? ps.docWidth2 : ps.docWidth;
        const bottomH = ps.docHeight2 > 0 ? ps.docHeight2 : ps.docHeight;
        setDoc2Width(String(bottomW));
        setDoc2Height(String(bottomH));
        runInAction(() => {
            ps.caf_valid_sizes = isValidDocSize(ps.docWidth, ps.docHeight) && (!isDoubleDocument() || isValidDocSize(bottomW, bottomH));
        });
        return () => {
            if (renderTimeout.current !== null) clearTimeout(renderTimeout.current);
            // The mobile preset path must still ask for the exact size at checkout.
            runInAction(() => {
                ps.caf_valid_sizes = false;
            });
        };
    }, [isDesktopCaf]);

    const commitDimensions = (width: string, height: string, bottomWidth: string, bottomHeight: string) => {
        if (renderTimeout.current !== null) clearTimeout(renderTimeout.current);
        const w = Number(width);
        const h = Number(height);
        const w2 = Number(bottomWidth);
        const h2 = Number(bottomHeight);
        const double = isDoubleDocument();
        runInAction(() => {
            s.ps.caf_valid_sizes = isValidDocSize(w, h) && (!double || isValidDocSize(w2, h2));
            if (!s.ps.caf_valid_sizes) return;
            s.ps.docWidth = w;
            s.ps.docHeight = h;
            s.ps.is_horizontal = w > h;
            s.prod.IsHorizontal = s.ps.is_horizontal;
            s.prod.OrientationHorizontal = s.ps.is_horizontal;
            updateDocSizeOptions(double ? {width: w2, height: h2} : undefined);
            renderTimeout.current = setTimeout(() => {
                runInAction(() => {
                    s.ui.doRender = true;
                    s.ui.update_pricing = true;
                    s.pulse();
                });
            }, 600);
        });
    };

    const changeDimension = (dimension: 'width' | 'height', value: string) => {
        const nextWidth = dimension === 'width' ? value : docWidth;
        const nextHeight = dimension === 'height' ? value : docHeight;
        setDocWidth(nextWidth);
        setDocHeight(nextHeight);
        // Until the shopper picks a bottom size of their own, the bottom document follows the top.
        const nextBottomWidth = doc2Linked.current ? nextWidth : doc2Width;
        const nextBottomHeight = doc2Linked.current ? nextHeight : doc2Height;
        setDoc2Width(nextBottomWidth);
        setDoc2Height(nextBottomHeight);
        commitDimensions(nextWidth, nextHeight, nextBottomWidth, nextBottomHeight);
    };

    const changeBottomDimension = (dimension: 'width' | 'height', value: string) => {
        doc2Linked.current = false;
        const nextWidth = dimension === 'width' ? value : doc2Width;
        const nextHeight = dimension === 'height' ? value : doc2Height;
        setDoc2Width(nextWidth);
        setDoc2Height(nextHeight);
        commitDimensions(docWidth, docHeight, nextWidth, nextHeight);
    };

    const sizeOptions = cafDocSizeOptions();
    const renderDimensionSelects = (doc: 'top' | 'bottom') => (
        <div style={{display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '6px 12px'}}>
            {(['width', 'height'] as const).map(dimension => {
                const current = doc === 'bottom' ? (dimension === 'width' ? doc2Width : doc2Height) : dimension === 'width' ? docWidth : docHeight;
                const hasValidCurrent = isValidCafDocDimension(Number(current));
                const id = doc === 'bottom' ? `caf-doc2-${dimension}` : `caf-doc-${dimension}`;
                const docLabel = isDoubleDocument() ? `${doc === 'bottom' ? 'Bottom' : 'Top'} document` : 'Document';
                return (
                    <div
                        key={dimension}
                        style={{display: 'flex', alignItems: 'center', gap: 6}}
                    >
                        <label
                            htmlFor={id}
                            style={{marginBottom: 0, whiteSpace: 'nowrap'}}
                        >
                            {dimension === 'width' ? 'Width:' : 'Height:'}
                        </label>
                        <select
                            id={id}
                            className="form-select"
                            style={{width: 96, height: 38}}
                            aria-label={`${docLabel} ${dimension} in inches`}
                            aria-invalid={!hasValidCurrent}
                            value={hasValidCurrent ? String(Number(current)) : ''}
                            onChange={e => (doc === 'bottom' ? changeBottomDimension(dimension, e.target.value) : changeDimension(dimension, e.target.value))}
                        >
                            {!hasValidCurrent && (
                                <option
                                    value=""
                                    disabled
                                >
                                    Select
                                </option>
                            )}
                            {sizeOptions.map(size => (
                                <option
                                    key={size}
                                    value={String(size)}
                                >
                                    {formatCafDocSize(size)}
                                </option>
                            ))}
                        </select>
                    </div>
                );
            })}
        </div>
    );

    const changeDocSize = (e: React.ChangeEvent<HTMLSelectElement>) => {
        e.preventDefault();
        let dw: IDocSize;
        if (s.prod.DYF_FrameStyleID === 10) {
            dw = doc_sizes_photo[parseInt(e.target.value, 10)];
        } else if (s.prod.DYF_FrameStyleID === 4) {
            dw = doc_sizes_varsity[parseInt(e.target.value, 10)];
        } else {
            dw = doc_sizes[parseInt(e.target.value, 10)];
        }
        const _w: number = parseFloat(dw.w);
        const _h: number = parseFloat(dw.h);

        s.ps.docWidth = _w;
        s.ps.docHeight = _h;
        if (s.ps.is_horizontal && s.ps.docWidth < s.ps.docHeight) {
            const newHeight = s.ps.docWidth;
            s.ps.docWidth = s.ps.docHeight;
            s.ps.docHeight = newHeight;
        } else if (!s.ps.is_horizontal && s.ps.docWidth > s.ps.docHeight) {
            const newHeight = s.ps.docWidth;
            s.ps.docWidth = s.ps.docHeight;
            s.ps.docHeight = newHeight;
        }
        updateDocSizeOptions();

        s.ui.doRender = true;
        s.ui.update_pricing = true;
        s.pulse();
    };

    const changeOrientation = (e: React.ChangeEvent<HTMLSelectElement>) => {
        s.ps.is_horizontal = !s.ps.is_horizontal;
        s.prod.IsHorizontal = s.ps.is_horizontal;
        s.prod.OrientationHorizontal = s.ps.is_horizontal;
        if (s.ps.is_horizontal && s.ps.docWidth < s.ps.docHeight) {
            const newHeight = s.ps.docWidth;
            s.ps.docWidth = s.ps.docHeight;
            s.ps.docHeight = newHeight;
        } else if (!s.ps.is_horizontal && s.ps.docWidth > s.ps.docHeight) {
            const newHeight = s.ps.docWidth;
            s.ps.docWidth = s.ps.docHeight;
            s.ps.docHeight = newHeight;
        }
        s.ps.docWidth2 = s.ps.docWidth;
        s.ps.docHeight2 = s.ps.docHeight;
        s.ui.doRender = true;
        s.ui.update_pricing = true;
        s.pulse();
    };

    const changeDYF = (e: React.ChangeEvent<HTMLSelectElement>) => {
        var _dest = e.target.value.replace('cf-', '').replace('mf-', '').replace('fr-', '');
        openLink(_dest);
    };

    if (isFixedSizeDisplay(s.prod)) return null;

    if (!s.ps.is_caf) {
        if (s.ps.docWidth <= 0) {
            return null;
        }

        return (
            <div id="doc-size">
                <ModalSimple
                    id="modal-doc-size"
                    maxWidth="800px"
                    title={`${s.ui.doc_type} Size Information`}
                >
                    <picture>
                        <source
                            type="image/webp"
                            srcSet={`https://${Portal?.CDNDomain}/images/cached/general/how_to_measure_document.webp`}
                        />
                        <img
                            src={`https://${Portal?.CDNDomain}/images/cached/general/how_to_measure_document.png`}
                            alt="Document Size Information"
                            aria-label="Document size refers to the dimensions of the entire document"
                            style={{width: '100%'}}
                        />
                    </picture>
                </ModalSimple>
                <DocSizeDiffModal />
                <div className="prod-doc-size-display">
                    <div>
                        <span
                            className="header-std"
                            style={{
                                paddingRight: '0.25rem',
                            }}
                        >
                            {s.ui.doc_type} Size:{' '}
                        </span>
                        {s.ps.docWidth}"w x {s.ps.docHeight}"h{' '}
                        <InfoIcon
                            id="modal-doc-size"
                            ariaLabel="View information about document size for this product"
                        />
                    </div>
                    <div>
                        <button
                            className="link link-btn"
                            onClick={() => modal('modal-doc-size-diff')}
                            type="button"
                            aria-label="View information about different document sizes"
                            aria-haspopup="dialog"
                            aria-controls="modal-doc-size-diff"
                        >
                            Different Size?
                        </button>
                    </div>
                    {s.si != null && s.si.Price_Retail != null && !s.isRetail && <div>Retail: {currency(s.si.Price_Retail)}</div>}
                </div>
            </div>
        );
    } else {
        return (
            <div
                className="gray-box caf-doc mt-2"
                style={{marginBottom: '0.5rem'}}
            >
                <ModalSimple
                    id="modal-doc-size-caf"
                    title={isDesktopCaf ? `${s.prod.DocType} Size Information` : "Don't See Your Size?"}
                >
                    {isDesktopCaf ? (
                        <div>
                            <p>Your frame is priced and previewed from the size you enter.</p>
                            <p>Enter a width and height from 5" to 24" in 1/8" steps. Orientation is set automatically from width versus height.</p>
                            {isDoubleDocument() && <p>This frame holds two {s.prod.DocType.toLowerCase()}s. The bottom size matches the top until you choose a different bottom width or height.</p>}
                        </div>
                    ) : (
                        <div>
                            <p>No worries! We'll ask for the exact size once you Add To Cart. In the meantime, we'll use the 'Up to' size to price your frame.</p>
                            <p>All of our frames are custom made to order so we can very easily create a frame that fits your specific Document size.</p>
                        </div>
                    )}
                </ModalSimple>
                <div
                    className="row"
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: isDesktopCaf ? (isDoubleDocument() ? 'flex-start' : 'flex-end') : undefined,
                    }}
                >
                    <div className="col-12 col-sm-7">
                        {isDesktopCaf ? (
                            <>
                                <div style={{fontSize: '15px', fontWeight: 700, marginBottom: 4}}>
                                    {isDoubleDocument() ? `Top ${s.prod.DocType} Size:` : `${s.prod.DocType} Size:`}{' '}
                                    <InfoIcon
                                        id="modal-doc-size-caf"
                                        ariaLabel="View information about document size for this product"
                                    />
                                </div>
                                {renderDimensionSelects('top')}
                                {isDoubleDocument() && (
                                    <>
                                        <div style={{fontSize: '15px', fontWeight: 700, marginBottom: 4, marginTop: 10}}>Bottom {s.prod.DocType} Size:</div>
                                        {renderDimensionSelects('bottom')}
                                    </>
                                )}
                            </>
                        ) : (
                            <>
                                <div style={{fontSize: '15px', fontWeight: 700}}>
                                    {s.prod.DocType} Size &amp; Orientation{' '}
                                    <InfoIcon
                                        id="modal-doc-size-caf"
                                        ariaLabel="View information about document size for this product"
                                    />
                                </div>
                                <select
                                    className="cust-entity"
                                    aria-label="Select document size"
                                    onChange={changeDocSize}
                                    style={{width: 230, display: 'block'}}
                                >
                                    {s.prod.DYF_FrameStyleID !== 10 &&
                                        s.prod.DYF_FrameStyleID !== 4 &&
                                        doc_sizes.map((m, i) => (
                                            <option
                                                key={i}
                                                selected={m.i === 3}
                                                value={m.i}
                                            >
                                                {s.ps.is_horizontal ? `Up to ${m.w}"w x ${m.h}"h` : `Up to ${m.h}"w x ${m.w}"h`}
                                            </option>
                                        ))}
                                    {s.prod.DYF_FrameStyleID === 10 &&
                                        doc_sizes_photo.map((m, i) => (
                                            <option
                                                key={i}
                                                selected={m.i === 0}
                                                value={m.i}
                                            >
                                                {s.ps.is_horizontal ? `Up to ${m.w}"w x ${m.h}"h` : `Up to ${m.h}"w x ${m.w}"h`}
                                            </option>
                                        ))}
                                    {s.prod.DYF_FrameStyleID === 4 &&
                                        doc_sizes_varsity.map((m, i) => (
                                            <option
                                                key={i}
                                                selected={m.i === 0}
                                                value={m.i}
                                            >
                                                {s.ps.is_horizontal ? `Up to ${m.w}"w x ${m.h}"h` : `Up to ${m.h}"w x ${m.w}"h`}
                                            </option>
                                        ))}
                                </select>
                                <select
                                    className="cust-entity"
                                    aria-label="Select document orientation"
                                    onChange={changeOrientation}
                                    style={{width: 230, display: 'block'}}
                                >
                                    <option
                                        value={'horizontal'}
                                        selected={s.ps.is_horizontal}
                                    >
                                        Horizontal
                                    </option>
                                    <option
                                        value={'vertical'}
                                        selected={!s.ps.is_horizontal}
                                    >
                                        Vertical
                                    </option>
                                </select>
                            </>
                        )}
                    </div>
                    {s.prod && s.prod.dyfList && (
                        <div className="col-12 col-sm-5">
                            <div style={{fontSize: '15px', fontWeight: 700, marginBottom: isDesktopCaf ? 4 : 0}}>{isDesktopCaf ? 'Frame Style:' : 'Frame Style'}</div>
                            <select
                                className={isDesktopCaf ? 'form-select' : 'cust-entity'}
                                aria-label="Select frame style"
                                onChange={changeDYF}
                                style={isDesktopCaf ? {maxWidth: 230, width: 230, height: 38} : {maxWidth: 230, width: 230}}
                            >
                                {s.prod.dyfList.map((m, i) => (
                                    <option
                                        key={i}
                                        value={m.id}
                                        selected={m.i === s.prod.ProductID}
                                    >
                                        {m.title}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}
                </div>
            </div>
        );
    }
});

export default DocSize;
